import { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { getColumnHNodes, getColumnWidths } from '../columns/useColumnWidths';
import useMultiSelect from '../../../hooks/useMultiSelect';

const noop = () => {};

/**
 * Shared business logic for GL2025 HeaderRow component (web and native)
 * Handles column processing, multi-select, and checkbox state
 */
export function useHeaderRowLogic(props, styleSource) {
    const {
        hNode = {},
        hNode: { includeCheckbox, singleRowSelect, namespace, relation } = {},
        headerClicked = noop,
        sortedColumns = {},
        columnHNodes: _columnHNodes,
        handleColumnWidthChange = noop,
        isPinned,
        borderRadiusTopLeft = true,
        borderRadiusTopRight = true
    } = props || { hNode: {} };
    
    // For two-grid: only show checkbox in pinned grid (or if isPinned not specified for backwards compat)
    const showCheckbox = isPinned !== false && includeCheckbox;
    
    const theme = useTheme();
    const { darkMode = false } = theme;
    
    // Get styles from appropriate source (tableStyle or nativeTableStyle)
    const styles = styleSource[darkMode ? 'dark' : 'light'].header;
    
    // Process column configurations
    const columnHNodes = useMemo(
        () => getColumnHNodes(_columnHNodes, hNode.children),
        [_columnHNodes, hNode.children]
    );
    
    const columnWidths = useMemo(
        () => getColumnWidths(columnHNodes, theme),
        [columnHNodes, theme]
    );
    
    // Multi-select functionality
    const { onChangeEverything, invertSelection, isSelectionDisabled } = useMultiSelect({
        namespace,
        relation,
        singleRowSelect
    });
    
    // Checkbox state calculations
    const checkboxState = useMemo(() => {
        const isIndeterminate = invertSelection === null;
        const isChecked = invertSelection === true;
        const hasState = isChecked || isIndeterminate;
        
        return {
            isIndeterminate,
            isChecked,
            hasState,
            iconName: isChecked ? 'check' : 'minus'
        };
    }, [invertSelection]);
    
    // Helper to get sort state for a column
    const getSortState = (column) => {
        const isSorted = sortedColumns[column.id];
        const columnState = isSorted ? 'sorted' : 'default';
        return { isSorted, columnState };
    };
    
    // Helper to handle column click
    const handleColumnClick = (column) => {
        if (column.sortable) {
            headerClicked(column);
        }
    };
    
    return {
        // Computed values
        showCheckbox,
        darkMode,
        styles,
        columnHNodes,
        columnWidths,
        borderRadiusTopLeft,
        borderRadiusTopRight,
        
        // Multi-select state
        invertSelection,
        onChangeEverything,
        selectionDisabled: isSelectionDisabled(),
        singleRowSelect,
        
        // Checkbox state
        checkboxState,
        
        // Helpers
        getSortState,
        handleColumnClick,
        handleColumnWidthChange
    };
}
